import { useEffect, useMemo, useRef, useState } from "react";
import { endOfWeek, format, startOfWeek } from "date-fns";
import { Plus, Trash2, Receipt, Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Field } from "../../components/Shared";
import { AuthImage } from "../../components/AuthImage";
import { api, eur, formatApiError } from "../../lib/api";
import { cachedGet, queuedMessage, submit } from "../../lib/offline";
import { EXPENSE_CATEGORIES } from "../../lib/labels";
import { useAuth } from "../../context/AuthContext";

const iso = (d) => format(d, "yyyy-MM-dd");

function ExpenseForm({ date, moves, onSaved }) {
  const [form, setForm] = useState({ category: "carburant", amount: "", description: "", move_id: "" });
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await submit({ url: "/field/expenses", method: "post", form: { ...form, date, receipt } });
      toast.success(queuedMessage(res, "Dépense enregistrée"));
      setForm({ ...form, amount: "", description: "" });
      setReceipt(null);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid="expense-form">
      <div className="flex flex-wrap gap-2">
        {Object.entries(EXPENSE_CATEGORIES).map(([k, l]) => (
          <button type="button" key={k} data-testid={`expense-cat-${k}`} onClick={() => setForm({ ...form, category: k })} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${form.category === k ? "bg-[#0b2c6b] text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Montant TTC (€)"><Input data-testid="expense-amount-input" type="number" inputMode="decimal" step="0.01" min="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
        <Field label="Chantier (optionnel)">
          <select data-testid="expense-move-select" className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm" value={form.move_id} onChange={(e) => setForm({ ...form, move_id: e.target.value })}>
            <option value="">Aucun</option>
            {moves.map((m) => <option key={m.id} value={m.id}>{m.title || m.client_name}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Description"><Input data-testid="expense-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex : plein gasoil A7" /></Field>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => setReceipt(e.target.files?.[0] || null)} data-testid="expense-receipt-input" />
      <div className="flex gap-2">
        <Button type="button" variant="outline" data-testid="expense-receipt-button" onClick={() => fileRef.current.click()} className="flex-1"><Camera className="mr-2 h-4 w-4" />{receipt ? "Justificatif ajouté" : "Photo du ticket"}</Button>
        <Button type="submit" disabled={busy} data-testid="expense-submit-button" className="flex-1 bg-[#0b2c6b]">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}Ajouter</Button>
      </div>
    </form>
  );
}

export default function NomadeExpenses() {
  const { user } = useAuth();
  const [date, setDate] = useState(iso(new Date()));
  const [expenses, setExpenses] = useState([]);
  const [moves, setMoves] = useState([]);
  const [open, setOpen] = useState(null);
  const ref = useMemo(() => new Date(`${date}T12:00:00`), [date]);
  const ws = iso(startOfWeek(ref, { weekStartsOn: 1 }));
  const we = iso(endOfWeek(ref, { weekStartsOn: 1 }));

  const load = () => cachedGet("/field/expenses", { start: ws, end: we }).then((r) => setExpenses(r.data)).catch(() => {});
  useEffect(() => {
    load();
    cachedGet("/field/moves", { start: date, end: date }).then((r) => setMoves(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const day = expenses.filter((x) => x.date === date);
  const sum = (l) => l.reduce((s, x) => s + x.amount, 0);
  const remove = async (x) => {
    if (!window.confirm("Supprimer cette dépense ?")) return;
    await api.delete(`/field/expenses/${x.id}`);
    load();
  };

  return (
    <div className="fade-up space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div><h1 className="text-xl font-bold text-slate-900">Dépenses</h1><p className="text-xs text-slate-500">{user.role === "equipier" ? "Vos frais du jour" : "Frais de toute l'équipe"}</p></div>
        <Input type="date" data-testid="expense-date-input" className="w-44 bg-white" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-[#0b2c6b] p-4 text-white"><p className="text-xs text-white/60">Total du jour</p><p className="font-mono-num text-2xl font-bold" data-testid="expense-day-total">{eur(sum(day))}</p></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4"><p className="text-xs text-slate-500">Total de la semaine</p><p className="font-mono-num text-2xl font-bold text-[#0b2c6b]" data-testid="expense-week-total">{eur(sum(expenses))}</p></div>
      </div>
      <ExpenseForm date={date} moves={moves} onSaved={load} />
      <ul className="space-y-2" data-testid="expense-list">
        {day.map((x) => (
          <li key={x.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`expense-${x.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-slate-900">{EXPENSE_CATEGORIES[x.category]} <span className="font-normal text-slate-500">· {x.user_name}</span></p>
                <p className="text-xs text-slate-500">{x.description || "—"}{x.move_title && ` · ${x.move_title}`}</p>
              </div>
              <div className="flex items-center gap-1">
                <span className="font-mono-num font-bold text-[#0b2c6b]">{eur(x.amount)}</span>
                {x.receipt_file_id && <Button size="icon" variant="ghost" data-testid={`expense-receipt-${x.id}`} onClick={() => setOpen(open === x.id ? null : x.id)}><Receipt className="h-4 w-4" /></Button>}
                <Button size="icon" variant="ghost" data-testid={`expense-delete-${x.id}`} onClick={() => remove(x)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            </div>
            {open === x.id && <AuthImage fileId={x.receipt_file_id} alt="Justificatif" className="mt-2 max-h-72 w-full rounded-lg object-contain" />}
          </li>
        ))}
        {day.length === 0 && <p className="py-6 text-center text-sm text-slate-400" data-testid="expense-empty">Aucune dépense ce jour</p>}
      </ul>
    </div>
  );
}
